import * as React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import cx from 'classnames';
import { compose, bindActionCreators, Dispatch } from 'redux';
import { Button } from 'reactstrap';
import RouteLink from './RouteLink';
import { Routes, makeFaqPath, FaqSection } from '../utils/routerHelper';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../utils/wrappers';
import actions from '../redux/actions';
import { State } from '../redux/state';
import * as selectors from '../redux/selectors';
import { UserRead, Entity, Competition } from '../userApi';
import NavBarDropdown from './NavBarDropdown';
import NotificationBell from './NotificationBell';
import { isProduction } from '../utils/environment';
import { campusesLabelFor, teamsLabelFor, logoPendingApproval } from '../utils/competitionLabels';

interface Props {
  isLoggedIn: boolean;
  hasJoined: boolean;
  user: UserRead;
  university: Entity;
  competition: Competition;
  canViewCompetitionContent: boolean;
  canManageEmailTemplates: boolean;
  canViewCommsTools: boolean;
  logout: () => void;
  competitionLogoUrl: string;
}

const buttonStyle = {
  width: '135px',
  height: '39px',
  marginRight: '14px'
};

const NavBar = ({
  t, history, logout, user, goToRouteByName, isLoggedIn, hasJoined,
  university, competition, canViewCompetitionContent, competitionLogoUrl, canManageEmailTemplates, canViewCommsTools
}: LProps<RouterProps<{}> & Props>) => {

  const campusesLabel = campusesLabelFor(competition, t('Campuses'));
  const teamsLabel = teamsLabelFor(competition, t('Teams'));
  const pendingLogo = logoPendingApproval(competition);
  return (
  <div id="nav_bar" role="navigation" aria-label={t('Main')}>
    <div className="cc__nav_bar__content">
      <div className="cc_nav_bar__logo">
        {competition && (
          <Link to={`/${competition.name}`}>
            {competitionLogoUrl && !pendingLogo ? (
              <img
                className={cx('cc__logo', { 'cc__logo--seal': competition.name === 'ccsf' })}
                src={competitionLogoUrl}
                alt={competition.name === 'ccsf'
                  ? t('City and County of San Francisco')
                  : (competition.description || t('Challenge logo'))}
              />
            ) : (
              <span className="cc__logo cc__logo--text" style={{ fontWeight: 700, fontSize: '0.95rem', color: '#064037' }}>
                {competition.description || t('Sustainability Challenge')}
              </span>
            )}
          </Link>
        )}

      </div>
      <div className="cc_nav_bar__tools">
        {!isLoggedIn &&
        <div className="nav_bar__web__buttons">
          <Button id="nav_bar__signup" className={'cc__rounded'} onClick={() => goToRouteByName(Routes.Signup)} style={buttonStyle}>
            {t('Get Started')}
          </Button>
          <Button id="nav_bar__login" className={'cc__rounded'} onClick={() => goToRouteByName(Routes.Login)} style={buttonStyle}>
            {t('Login')}
          </Button>
        </div>
        }
        {(!isLoggedIn || !competition ) &&
          <div id="nav_bar__web__links">
            {competition && <RouteLink activable={true} route={Routes.About}>{t('About')}</RouteLink>}
            {!competition && <RouteLink activable={true} route={Routes.GlobalAbout} >{t('About')}</RouteLink>}
            {!isProduction && competition && Boolean(competition.config && competition.config.blogUrl) && <RouteLink activable={true} route={Routes.Blog}>{t('Blog')}</RouteLink>}
            <Link className={cx({ inactive: history.location.pathname.search('faq') < 0 })} to={makeFaqPath(FaqSection.General, t)}>{t('FAQ')}</Link>
          </div>
        }
        <div className="cc__nav_bar__web__avatar">
          {competition && isLoggedIn && hasJoined && <NotificationBell />}
          {competition && <NavBarDropdown
            t={t}
            user={user}
            logout={logout}
            history={history}
            university={university}
            competition={competition}
            loggedIn={!!(isLoggedIn && user)}
            isProduction={isProduction}
            canManageEmailTemplates={canManageEmailTemplates}
            canViewCommsTools={canViewCommsTools}
            showMobilePrimaryNav={Boolean(isLoggedIn && canViewCompetitionContent)}
          />}
        </div>
        {(isLoggedIn && canViewCompetitionContent) &&
          <div id="nav_bar__web__links">
            <RouteLink activable={true} route={Routes.Dashboard}>{t('Home')}</RouteLink>
            <RouteLink activable={true} route={Routes.Pledges}>{t('Actions')}</RouteLink>
            <RouteLink activable={true} route={Routes.Departments}>{campusesLabel}</RouteLink>
            <RouteLink activable={true} route={Routes.Teams}>{teamsLabel}</RouteLink>
            <RouteLink activable={true} route={Routes.Calculator}>{t('Calculator')}</RouteLink>
          </div>
        }
      </div>
    </div>
  </div>
); };

const mapStateToProps = (state: State): Partial<Props> => ({
  isLoggedIn: selectors.isLoggedIn(state),
  hasJoined: selectors.hasJoined(state),
  user: state.user,
  university: selectors.userUniversity(state),
  competition: state.competition,
  canViewCompetitionContent: selectors.canViewCompetitionContent(state),
  canManageEmailTemplates: selectors.canViewAdminTools(state),
  canViewCommsTools: selectors.canViewCommsTools(state),
  competitionLogoUrl: selectors.competitionLogoUrl(state)
});

const mapDispatchToProps = (dispatch: Dispatch): Partial<Props> => bindActionCreators({
  logout: actions.logout
}, dispatch);

export default compose(
  withLocalization('navigation'),
  withRouterAndHelpers,
  connect(mapStateToProps, mapDispatchToProps)
)(NavBar);
